/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo } from 'react'
import { defineMessages } from 'react-intl'

import { FormattedMessage } from '@modules/i18n'
import type { RoutableBreadcrumb } from '@modules/ui-types'

import useUserOrganizationBreadcrumb from './OrganizationSwitch/OrganizationBreadcrumb'

const messages = defineMessages({
  elasticCloud: {
    id: 'portal.crumbs.elasticCloud',
    defaultMessage: 'Cloud home',
  },
})

const rootEssCloudCrumbs = (): RoutableBreadcrumb[] => [
  {
    text: <FormattedMessage {...messages.elasticCloud} />,
  },
]

const rootEceCrumbs = (): RoutableBreadcrumb[] => [
  {
    text: (
      <FormattedMessage
        id='crumb-builder.admin-console-ece'
        defaultMessage='Elastic Cloud Enterprise'
      />
    ),
  },
]

const rootEssCrumbs = (): RoutableBreadcrumb[] => [
  {
    text: (
      <FormattedMessage id='crumb-builder.admin-console' defaultMessage='Elasticsearch Service' />
    ),
  },
]

export const useGetRootByApp = ({
  appName,
  appType,
  isAtRoot,
}: {
  appName: string
  appType: string
  isAtRoot: boolean
}): RoutableBreadcrumb[] | undefined => {
  const userOrganizationBreadcrumb = useUserOrganizationBreadcrumb()

  const rootCrumbs = useMemo(() => {
    if (appName === `userconsole`) {
      return isAtRoot
        ? [userOrganizationBreadcrumb, ...rootEssCloudCrumbs()]
        : [userOrganizationBreadcrumb]
    }

    if (appType === `cloud-enterprise-adminconsole`) {
      return rootEceCrumbs()
    }

    return rootEssCrumbs()
  }, [appName, appType, isAtRoot, userOrganizationBreadcrumb])

  return rootCrumbs
}
